import React from 'react';
import { useObservable } from 'react-use-observable';
import { AutoSizer, List as VList } from 'react-virtualized';
import type { ListRowProps } from 'react-virtualized';

import { Checkbox } from 'antd';

import { useFormikContext } from 'formik';
import type { IAffiliateOption } from 'v2/interfaces/affiliate/selectAffiliate';
import selectAffiliateService from 'v2/services/affiliate/selectAffiliate';

interface IProps {
  affiliates: IAffiliateOption[];
  selectedQuantity: number;
  selectedAffiliates: IAffiliateOption[];
}

type RowRenderExtends = 'key' | 'index';

interface RowRendererProps extends Pick<ListRowProps, RowRenderExtends> {
  style?: React.CSSProperties;
}

export const List = ({ affiliates, selectedAffiliates }: IProps) => {
  const { setFieldValue } = useFormikContext();

  const [isLoadingAffiliates] = useObservable(() => selectAffiliateService.getIsLoading(), []);

  const selectedAffiliatesId = selectedAffiliates.map(aff => aff.id);

  const handleSelectAffiliate = (affiliate: IAffiliateOption) => {
    const isAlreadySelected = selectedAffiliates.some(aff => aff.id === affiliate.id);

    if (isAlreadySelected) {
      const filteredAffiliates = selectedAffiliates.filter(aff => aff.id !== affiliate.id);

      setFieldValue('affiliates', filteredAffiliates);

      return;
    }

    setFieldValue('affiliates', [...selectedAffiliates, affiliate]);
  };

  const rowRenderer = ({ key, index, style }: RowRendererProps) => {
    const affiliate = affiliates[index];

    return (
      <div
        key={key}
        className='flex cursor-pointer items-center px-2 py-2 hover:bg-gray-50'
        style={style}
        onClick={() => handleSelectAffiliate(affiliate)}
      >
        <Checkbox checked={selectedAffiliatesId.includes(affiliate.id)} className='mr-2' />
        <div className='ml-4'>
          <p className='mb-0! font-medium'>{affiliate.name}</p>
          <p className='mb-0! text-sm text-gray-500'>{affiliate.email}</p>
        </div>
      </div>
    );
  };

  const affiliatesSize = affiliates.length;
  const rowsLimit = 50;

  return (
    <>
      {!!affiliatesSize && affiliatesSize < rowsLimit && (
        <ul className='h-[calc(80vh-240px)] overflow-auto rounded-sm border border-[rgb(221,221,221)] max-sm:h-[calc(95vh-280px)]'>
          {affiliates.map((affiliate, index) => rowRenderer({ key: String(affiliate.id), index }))}
        </ul>
      )}
      {!!affiliatesSize && affiliatesSize >= rowsLimit && (
        <AutoSizer>
          {({ height, width }) => (
            <VList
              height={height}
              width={width}
              rowRenderer={rowRenderer}
              rowHeight={72}
              rowCount={affiliates.length}
            />
          )}
        </AutoSizer>
      )}

      {!isLoadingAffiliates && !affiliatesSize && (
        <div className='animate-fade-in p-4 text-center'>
          <p>Nenhum afiliado encontrado</p>
        </div>
      )}

      {isLoadingAffiliates && (
        <div className='animate-fade-in p-4 text-center'>
          <p>Carregando afiliados...</p>
        </div>
      )}
    </>
  );
};
